import { ReactNode, useCallback, useMemo } from 'react'
import { AccountTypeNames } from '@medentee/enums'
import cls from 'classnames'
import { useTranslation } from 'react-i18next'

import { EAvatarSize } from 'enums'
import {
  Avatar,
  Button,
  Chip,
  IconLabel,
  TAvatarProps,
  TIconLabelProps,
  UploadCover
} from 'App/components/common'
import { IEventDTO } from 'interfaces'
import { ReactComponent as ChevronLeftIcon } from 'assets/icons/ChevronLeft.svg'
import { useAdaptiveLayout } from 'App/hooks/useAdaptiveLayout'
import { showExtendableCardAction } from 'store/extendableCard/extendableCard.actions'
import { EExtendableCardType } from 'store/extendableCard/extendableCard.types'
import { useAppDispatch } from 'store/store.utils'

import { UnityHubInfoSkeleton } from './UnityHubInfoSkeleton'
import styles from './UnityHubInfo.module.scss'
import { TUnityHubInfoOwnerProps, UnityHubInfoOwner } from './UnityHubInfoOwner'
import { EUnityHubSharePopoverType, UnityHubSharePopover } from './UnityHubSharePopover'

type TUnityHubInfoProps = Pick<TAvatarProps, 'invitationToken' | 'isCommunity'> &
  Pick<TUnityHubInfoOwnerProps, 'socialLinks' | 'organizer' | 'provider'> & {
    details: TIconLabelProps[]
    title: string
    aboutTitle: string
    joinButtonLabel: string
    shouldShowJoin: boolean
    shouldShowGoToChannels: boolean
    isFetching: boolean
    isJoinProcessing: boolean
    isPublic: boolean
    onJoin: () => void
    onGoToChannels: () => void

    id?: string
    coverFileUrl?: string
    about?: string
    headline?: ReactNode
    members?: ReactNode
    tags?: string[]
    event?: {
      dateLabel: string | null | undefined
    } & Pick<IEventDTO, 'settings' | 'isViewableCommunity'>
    allowFullscreen?: boolean
  }

const FULLSCREEN_SLIDE_ID = '1'

export const UnityHubInfo = ({
  id,
  invitationToken,
  organizer,
  provider,
  details,
  headline,
  members,
  tags,
  coverFileUrl,
  title,
  about,
  aboutTitle,
  joinButtonLabel,
  socialLinks,
  shouldShowJoin,
  shouldShowGoToChannels,
  isCommunity,
  event,
  isFetching,
  isJoinProcessing,
  isPublic,
  allowFullscreen,
  onJoin,
  onGoToChannels
}: TUnityHubInfoProps) => {
  const { isTablet, isMobile } = useAdaptiveLayout()

  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const sharePageData = useMemo(() => {
    if (isCommunity) {
      return { text: title, type: EUnityHubSharePopoverType.COMMUNITY }
    }

    if (event) {
      return { text: `${event.dateLabel} ${title}`, type: EUnityHubSharePopoverType.EVENT }
    }

    return null
  }, [event, title, isCommunity])

  const avatarSize = useMemo(() => {
    if (isMobile) {
      return EAvatarSize.MD
    }

    if (isTablet) {
      return EAvatarSize.LG
    }

    return EAvatarSize.LLG
  }, [isMobile, isTablet])

  const handleCoverClick = useCallback(() => {
    if (!allowFullscreen || typeof coverFileUrl !== 'string') {
      return
    }

    dispatch(
      showExtendableCardAction({
        type: EExtendableCardType.FILE_VIEWER,
        initialSlideId: FULLSCREEN_SLIDE_ID,
        slides: [{ extension: 'png', id: FULLSCREEN_SLIDE_ID, fileSrc: coverFileUrl }]
      })
    )
  }, [allowFullscreen, coverFileUrl, dispatch])

  const shouldShowShare = isPublic && sharePageData && id
  const shouldShowActions = shouldShowJoin || shouldShowShare

  const getAside = () => {
    const content = (
      <>
        {shouldShowActions && (
          <div className={styles.actions}>
            {shouldShowJoin && (
              <Button className={styles.action} onClick={onJoin} loading={isJoinProcessing}>
                {joinButtonLabel}
              </Button>
            )}

            {shouldShowShare && (
              <UnityHubSharePopover id={id} text={sharePageData.text} type={sharePageData.type} />
            )}
          </div>
        )}

        <div className={styles.owner}>
          {members}

          <UnityHubInfoOwner
            socialLinks={socialLinks}
            organizer={organizer}
            provider={provider}
            communityId={event?.isViewableCommunity ? event.settings.representative.id : undefined}
          />
        </div>
      </>
    )

    return isMobile ? content : <div className={styles.aside}>{content}</div>
  }

  if (isFetching || !organizer || !id || !coverFileUrl) {
    return (
      <div className={styles.root}>
        <UnityHubInfoSkeleton />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.images}>
        <div className={styles.cover}>
          <UploadCover file={coverFileUrl} onCoverClick={handleCoverClick} />
        </div>

        <Avatar
          invitationToken={invitationToken}
          firstName=""
          lastName=""
          size={avatarSize}
          isEvent={!!event}
          isCommunity={isCommunity}
          userId={id}
          type={AccountTypeNames.BUSINESS}
          displayUserName={title}
          border={true}
          className={styles.avatar}
        />
      </div>

      <div
        className={cls(styles.content, {
          [styles.content]: about,
          [styles.withActions]: shouldShowActions && !about,
          [styles.withAbout]: about && !shouldShowActions,
          [styles.complete]: about && shouldShowActions
        })}
      >
        <div className={styles.head}>
          {shouldShowGoToChannels && (
            <Button
              variant="inline"
              fontWeight="bold"
              icon={<ChevronLeftIcon />}
              onClick={onGoToChannels}
            >
              {t('unityHub.goToChannelsButton')}
            </Button>
          )}
          <h3 className={styles.title}>{title}</h3>

          {headline}
        </div>

        {getAside()}

        <div className={styles.user}>
          {details.map(({ icon, label, align }, index) => (
            <IconLabel
              key={index}
              icon={icon}
              label={label}
              align={align}
              classes={{
                root: styles.details,
                label: styles.detailsLabel,
                icon: styles.detailsIcon
              }}
            />
          ))}

          {!!tags?.length && (
            <div className={styles.tags}>
              {tags.map((tag, index) => (
                <Chip variant="light" key={index} text={tag} className={styles.tag} />
              ))}
            </div>
          )}
        </div>

        {about && (
          <div className={styles.about}>
            <h3 className={styles.aboutTitle}>{aboutTitle}</h3>

            <div>{about}</div>
          </div>
        )}
      </div>
    </div>
  )
}
